import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useIsMounted from '../../hooks/useIsMounted.mjs';

export default function Modal({ isOpen, onClose, title, children }) {
    const modalRef = useRef(null);
    const isMounted = useIsMounted();

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        const handleClickOutside = (e) => {
            if (modalRef.current && !modalRef.current.contains(e.target) && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.addEventListener('mousedown', handleClickOutside);
            // Prevent scrolling on body when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.removeEventListener('mousedown', handleClickOutside);
            // Only reset overflow if we set it to hidden
            if (isOpen) {
                document.body.style.overflow = 'auto';
            }
        };
    }, [isOpen, onClose]);

    // Don't render anything on server-side
    if (!isMounted) return null;

    // Use createPortal to render the modal at the end of the document body
    return createPortal(
        <div className={`modal-overlay ${isOpen ? 'modal-visible' : 'modal-hidden'}`}>
            <div className="modal-container">
                <div className="modal-content" ref={modalRef}>
                    <div className="modal-header">
                        <h2 className="modal-title">{title}</h2>
                        <button className="modal-close-button" onClick={onClose} aria-label="Close">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>
                    <div className="modal-body">{children}</div>
                </div>
            </div>
        </div>,
        document.body
    );
}
